import { KEY_SETTINGS_STORAGE } from '~/constants';
import { getFromSessionStorage } from '~/helpers/storage';
import { clickstreamLogger } from '~/logging';

interface SettingsItem {
  settingsDomain: string;
  settingsItems?: SettingsItem[];
  settingsCode: string;
  settingsHash: string;
  settingsValue: any;
}

const processSettingsItem = (item: SettingsItem) => {
  switch (item.settingsDomain.toUpperCase()) {
    case 'PARENT': {
      if (item.settingsItems && Array.isArray(item.settingsItems)) {
        return item.settingsItems.reduce((map: any, i: SettingsItem) => {
          const isEmpty = !map[i.settingsCode.toUpperCase()];

          if (
            (isEmpty && i.settingsHash === '') ||
            i.settingsHash === location.hash
          ) {
            map[i.settingsCode.toUpperCase()] = processSettingsItem(i);
          }

          return map;
        }, {});
      } else {
        clickstreamLogger.warn(
          'PARENT settings item has no children: ',
          item
        );

        return [];
      }
    }

    case 'JSON': {
      try {
        return JSON.parse(item.settingsValue);
      } catch (e) {
        clickstreamLogger.error(
          'Invalid JSON passed in settings: ',
          item,
          e
        );

        return item.settingsValue;
      }
    }

    case 'DOUBLE':
    case 'INTEGER':
    case 'NUMBER': {
      const value =
        typeof item.settingsValue === 'number'
          ? item.settingsValue
          : item.settingsValue.indexOf('.') !== -1
            ? Number.parseFloat(item.settingsValue)
            : Number.parseInt(item.settingsValue, 10);
      if (value === value) {
        return value;
      }
      clickstreamLogger.error(
        'Invalid number passed in settings: ',
        item
      );

      return undefined;
    }
  }

  return item.settingsValue;
};

class Settings {

  private currentHash: string | null;

  public settingsMapping: { [key: string]: any };
 
  constructor() {
    this.currentHash = null;
    this.settingsMapping = {};
  }

  public init(): void {
    const settingsList = getFromSessionStorage(
      KEY_SETTINGS_STORAGE,
      'settings'
    );
    this.settingsMapping = {};

    if (settingsList && settingsList.length > 0) {
      clickstreamLogger.debug(
        'Settings processing started: ',
        settingsList
      );
      settingsList.forEach((item: SettingsItem) => {
        const isEmpty = !this.settingsMapping[item.settingsCode];

        if (
          (isEmpty && item.settingsHash === '') ||
          item.settingsHash === location.hash
        ) {
          this.settingsMapping[item.settingsCode] = processSettingsItem(item);
        }
      }, this);
      clickstreamLogger.debug(
        'Settings processing finished: ',
        this.settingsMapping
      );
    } else {
      clickstreamLogger.error(
        'Unable to process settings. Empty list passed.',
        settingsList
      );
    }
  }

  public get(settingsCode: string) {
    if (this.currentHash !== location.hash) {
      this.init();
      this.currentHash = location.hash;
    }

    if (settingsCode && typeof settingsCode === 'string') {
      return this.settingsMapping
        ? this.settingsMapping[settingsCode]
        : undefined;
    }
    clickstreamLogger.warn(
      'Invalid settings code passed: ',
      settingsCode
    );

    return undefined;
  }

  public getOrDefault(settingsCode: string, defaultValue: any) {
    const value = this.get(settingsCode);

    return typeof value !== 'undefined' ? value : defaultValue;
  }

  // If the setting is disabled, then the rule work for all domain (true)
  // else if the setting is enabled but this location doesn't match then false, else true.
  public isEnableSettingForLocation(settingsCode: string) {
    const isEnableForLocation = this.getOrDefault(settingsCode, 0);
    if (isEnableForLocation !== 0) {
      const regex = new RegExp(isEnableForLocation, 'gm');

      return !!location.pathname.match(regex);
    } else {
      return true;
    }
  }
}

const settings = new Settings();

export default settings;